// src/features/tuition/components/online/OnlineLearning.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';
import {
  MenuBook as SubjectIcon,
  VideoLibrary as TutorialIcon,
  Description as ResourceIcon,
  Assignment as AssessmentIcon,
  Search as SearchIcon,
  Topic as TopicIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CompletedIcon,
  Add as AddIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Article as ArticleIcon,
  School as CourseIcon,
  
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import SubjectForm from './SubjectForm';
import SubjectDetails from './SubjectDetails';
import TopicForm from './TopicForm';
import SubtopicForm from './SubtopicForm';
import TutorialManagement from './TutorialManagement';

const OnlineLearning = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('subjects');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [resources, setResources] = useState([]);
  const [assessments, setAssessments] = useState([]);
  
  // Form states
  const [isSubjectFormOpen, setIsSubjectFormOpen] = useState(false);
  const [isTopicFormOpen, setIsTopicFormOpen] = useState(false);
  const [isSubtopicFormOpen, setIsSubtopicFormOpen] = useState(false);
  
  // Selected items for forms
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  
  // View states
  const [viewSubjectId, setViewSubjectId] = useState(null);
  const [createContentMenuAnchorEl, setCreateContentMenuAnchorEl] = useState(null);

  useEffect(() => {
    // Load data
    loadData();
    
    // Mock data for demo
    setTimeout(() => {
      const mockSubjects = [
        { 
          id: '1', 
          name: 'Mathematics', 
          grade: '10',
          progress: 65,
          topics: 24,
          completed: 15,
          image: 'https://images.unsplash.com/photo-1635070041078-e363b712a58b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=250&q=80'
        },
        { 
          id: '2', 
          name: 'Physical Sciences', 
          grade: '11',
          progress: 42,
          topics: 30,
          completed: 12,
          image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=250&q=80'
        },
        { 
          id: '3', 
          name: 'English Language', 
          grade: '9',
          progress: 78,
          topics: 18,
          completed: 14,
          image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=250&q=80'
        },
        { 
          id: '4', 
          name: 'Biology', 
          grade: '12',
          progress: 30,
          topics: 28,
          completed: 8,
          image: 'https://images.unsplash.com/photo-1530210124550-912dc1381cb8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=250&q=80'
        }
      ];
      
      const mockTutorials = [
        {
          id: '1',
          title: 'Solving Quadratic Equations',
          subject: 'Mathematics',
          grade: '10',
          duration: '25 min',
          views: 145,
          completed: true,
          thumbnail: 'https://images.unsplash.com/photo-1596495578065-6e0763fa1178?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=250&q=80'
        },
        {
          id: '2',
          title: 'Balancing Chemical Equations',
          subject: 'Physical Sciences',
          grade: '11',
          duration: '32 min',
          views: 98,
          completed: false,
          thumbnail: 'https://images.unsplash.com/photo-1603126857599-f6e157fa2fe6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=250&q=80'
        },
        {
          id: '3',
          title: 'Literary Devices in Poetry',
          subject: 'English Language',
          grade: '9',
          duration: '18 min',
          views: 72,
          completed: true,
          thumbnail: 'https://images.unsplash.com/photo-1456413253251-cca71644b3cd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=250&q=80'
        },
        {
          id: '4',
          title: 'Cell Structure and Function',
          subject: 'Biology',
          grade: '12',
          duration: '45 min',
          views: 124,
          completed: false,
          // src/features/tuition/components/online/OnlineLearning.js (continued)
          thumbnail: 'https://images.unsplash.com/photo-1579154341098-e4e158cc7f55?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&h=250&q=80'
        }
      ];
      
      const mockResources = [
        {
          id: '1',
          title: 'Mathematics Formula Sheet',
          subject: 'Mathematics',
          grade: '10',
          type: 'PDF',
          downloads: 234,
          size: '1.2 MB'
        },
        {
          id: '2',
          title: 'Chemistry Periodic Table Reference',
          subject: 'Physical Sciences',
          grade: '11',
          type: 'PDF',
          downloads: 156,
          size: '3.5 MB'
        },
        {
          id: '3',
          title: 'English Grammar Guide',
          subject: 'English Language',
          grade: '9',
          type: 'DOCX',
          downloads: 98,
          size: '850 KB'
        },
        {
          id: '4',
          title: 'Cell Biology Diagrams',
          subject: 'Biology',
          grade: '12',
          type: 'PDF',
          downloads: 187,
          size: '5.4 MB'
        }
      ];
      
      const mockAssessments = [
        {
          id: '1',
          title: 'Algebra Mid-Term Test',
          subject: 'Mathematics',
          grade: '10',
          questions: 30,
          duration: '60 min',
          deadline: '2025-05-15',
          status: 'completed',
          score: '85%'
        },
        {
          id: '2',
          title: 'Chemical Reactions Quiz',
          subject: 'Physical Sciences',
          grade: '11',
          questions: 20,
          duration: '45 min',
          deadline: '2025-05-10',
          status: 'not-started'
        },
        {
          id: '3',
          title: 'Poetry Analysis Assignment',
          subject: 'English Language',
          grade: '9',
          questions: 15,
          duration: '30 min',
          deadline: '2025-05-20',
          status: 'completed',
          score: '92%'
        },
        {
          id: '4',
          title: 'Photosynthesis Quiz',
          subject: 'Biology',
          grade: '12',
          questions: 25,
          duration: '50 min',
          deadline: '2025-05-12',
          status: 'in-progress'
        }
      ];
      
      setSubjects(mockSubjects);
      setTutorials(mockTutorials);
      setResources(mockResources);
      setAssessments(mockAssessments);
      setLoading(false);
    }, 1000);
  }, [userId]);
  
  const loadData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    // In a real app, these would be API calls to fetch the data
    // setSubjects(await fetchSubjects(userId));
    // setTutorials(await fetchTutorials(userId));
    // setResources(await fetchResources(userId));
    // setAssessments(await fetchAssessments(userId));
  };
  
  const handleCreateContentClick = (event) => {
    setCreateContentMenuAnchorEl(event.currentTarget);
  };

  // Close "Create Content" dropdown menu
  const handleCreateContentMenuClose = () => {
    setCreateContentMenuAnchorEl(null);
  };

  // Add subject form handlers
  const handleAddSubject = () => {
    setSelectedSubject(null);
    setIsSubjectFormOpen(true);
    handleCreateContentMenuClose();
  };

  const handleEditSubject = (subject) => {
    setSelectedSubject(subject);
    setIsSubjectFormOpen(true);
    handleMenuClose();
  };

  const handleSubjectFormClose = () => {
    setIsSubjectFormOpen(false);
    setSelectedSubject(null);
  };

  // Add topic form handlers
  const handleAddTopic = () => {
    setSelectedTopic(null);
    setIsTopicFormOpen(true);
    handleCreateContentMenuClose();
  };

  const handleEditTopic = (topic) => {
    setSelectedTopic(topic);
    setIsTopicFormOpen(true);
    handleMenuClose();
  };

  const handleTopicFormClose = () => {
    setIsTopicFormOpen(false);
    setSelectedTopic(null);
  };

  // Add subtopic form handlers
  const handleAddSubtopic = () => {
    setSelectedTopic(null);
    setSelectedSubtopic(null);
    setIsSubtopicFormOpen(true);
    handleCreateContentMenuClose();
  };

  const handleEditSubtopic = (subtopic, topic) => {
    setSelectedTopic(topic);
    setSelectedSubtopic(subtopic);
    setIsSubtopicFormOpen(true);
    handleMenuClose();
  };

  const handleSubtopicFormClose = () => {
    setIsSubtopicFormOpen(false);
    setSelectedTopic(null);
    setSelectedSubtopic(null);
  };

  // Subject details view handlers
  const handleViewSubject = (subjectId) => {
    setViewSubjectId(subjectId);
  };
  
  const handleBackToSubjects = () => {
    setViewSubjectId(null);
  };
  
  const handleMenuOpen = (event, itemId) => {
    setAnchorEl(event.currentTarget);
    setSelectedItemId(itemId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItemId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Add this function to handle tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const getFilteredItems = () => {
    if (!searchTerm) {
      return {
        subjects,
        tutorials,
        resources,
        assessments
      };
    }
    
    const term = searchTerm.toLowerCase();
    
    return {
      subjects: subjects.filter(subject => 
        subject.name.toLowerCase().includes(term) || 
        subject.grade.toLowerCase().includes(term)
      ),
      tutorials: tutorials.filter(tutorial => 
        tutorial.title.toLowerCase().includes(term) || 
        tutorial.subject.toLowerCase().includes(term)
      ),
      resources: resources.filter(resource => 
        resource.title.toLowerCase().includes(term) || 
        resource.subject.toLowerCase().includes(term)
      ),
      assessments: assessments.filter(assessment => 
        assessment.title.toLowerCase().includes(term) || 
        assessment.subject.toLowerCase().includes(term)
      )
    };
  };
  
  const filteredItems = getFilteredItems();
  
  const getStatusChip = (status) => {
    switch (status) {
      case 'completed':
        return <Chip label="Completed" size="small" sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' }} />;
      case 'in-progress':
        return <Chip label="In Progress" size="small" sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' }} />;
      case 'not-started':
        return <Chip label="Not Started" size="small" sx={{ bgcolor: 'rgba(158, 158, 158, 0.1)', color: '#757575' }} />;
      default:
        return <Chip label={status} size="small" sx={{ bgcolor: 'rgba(158, 158, 158, 0.1)', color: '#757575' }} />;
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }
  
  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Online Learning
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => setCreateContentMenuAnchorEl(e.currentTarget)}
            sx={{
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            Create Content
          </Button>
          <Menu
            anchorEl={createContentMenuAnchorEl}
            open={Boolean(createContentMenuAnchorEl)}
            onClose={() => setCreateContentMenuAnchorEl(null)}
          >
            <MenuItem onClick={handleAddSubject}>
              <ListItemIcon>
                <SubjectIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Subject</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAddTopic} disabled={subjects.length === 0}>
              <ListItemIcon>
                <TopicIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Topic</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAddSubtopic} disabled={subjects.length === 0}>
              <ListItemIcon>
                <ArticleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Subtopic</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
        
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center" gap={2} mb={3}>
          <TextField
            fullWidth
            placeholder="Search content..."
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: { sm: '400px' } }}
          />
          
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTab-root': {
                minWidth: '120px',
                textTransform: 'none'
              },
              '& .Mui-selected': {
                color: `${THEME_COLORS.primary} !important`
              },
              '& .MuiTabs-indicator': {
                backgroundColor: THEME_COLORS.primary
              }
            }}
          >
            <Tab 
              icon={<SubjectIcon />} 
              iconPosition="start" 
              label="Subjects" 
              value="subjects" 
            />
            <Tab 
              icon={<TutorialIcon />} 
              iconPosition="start" 
              label="Tutorials" 
              value="tutorials" 
            />
            <Tab 
              icon={<ResourceIcon />} 
              iconPosition="start" 
              label="Resources" 
              value="resources" 
            />
            <Tab 
              icon={<AssessmentIcon />} 
              iconPosition="start" 
              label="Assessments" 
              value="assessments" 
            />
          </Tabs>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Content based on active tab */}
        {activeTab === 'subjects' && (
          viewSubjectId ? (
            <SubjectDetails 
              subjectId={viewSubjectId} 
              userId={userId} 
              onBack={handleBackToSubjects}
              onEditTopic={handleEditTopic}
              onEditSubtopic={handleEditSubtopic}
            />
          ) : (
            <Grid container spacing={2}>
              {filteredItems.subjects.length === 0 ? (
                <Grid item xs={12}>
                  <Box textAlign="center" py={4}>
                    <SubjectIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      No subjects found
                    </Typography>
                    <Typography color="text.secondary" paragraph>
                      {searchTerm ? 'Try changing your search criteria' : 'Add your first subject to get started'}
                    </Typography>
                    {!searchTerm && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddSubject}
                        sx={{
                          backgroundColor: THEME_COLORS.primary,
                          '&:hover': { backgroundColor: '#1e1c4e' },
                          mt: 2
                        }}
                      >
                        Add Subject
                      </Button>
                    )}
                  </Box>
                </Grid>
              ) : (
                filteredItems.subjects.map(subject => (
                  <Grid item xs={12} sm={6} md={4} key={subject.id}>
                    <Card 
                      onClick={() => handleViewSubject(subject.id)}
                      sx={{ 
                        height: '100%', 
                        borderRadius: 2, 
                        display: 'flex', 
                        flexDirection: 'column',
                        transition: 'all 0.2s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="140"
                        image={subject.image}
                        alt={subject.name}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {subject.name}
                          </Typography>
                          <Chip 
                            label={`Grade ${subject.grade}`} 
                            size="small"
                            sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                          />
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              Progress
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {subject.progress}%
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: '6px',
                              bgcolor: 'rgba(39, 35, 97, 0.08)',
                              borderRadius: 3,
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                width: `${subject.progress}%`,
                                height: '100%',
                                bgcolor: THEME_COLORS.primary,
                                borderRadius: 3
                              }}
                            />
                          </Box>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            {subject.completed}/{subject.topics} topics completed
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent Card onClick from firing
                              handleMenuOpen(e, subject.id);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          )
        )}
        
        {activeTab === 'tutorials' && (
          <Grid container spacing={3}>
            {filteredItems.tutorials.length === 0 ? (
              <Grid item xs={12}>
                <Box textAlign="center" py={4}>
                  <TutorialIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No tutorials found
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    {searchTerm ? 'Try changing your search criteria' : 'Add your first tutorial to get started'}
                  </Typography>
                  {!searchTerm && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{
                        backgroundColor: THEME_COLORS.primary,
                        '&:hover': { backgroundColor: '#1e1c4e' },
                        mt: 2
                      }}
                    >
                      Add Tutorial
                    </Button>
                  )}
                </Box>
              </Grid>
            ) : (
              filteredItems.tutorials.map(tutorial => (
                <Grid item xs={12} sm={6} md={4} key={tutorial.id}>
                  <Card sx={{ 
                    height: '100%', 
                    borderRadius: 2, 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 4
                    }
                  }}>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="160"
                        image={tutorial.thumbnail}
                        alt={tutorial.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          '&:hover': {
                            opacity: 1
                          }
                        }}
                      >
                        <IconButton
                          sx={{
                            bgcolor: 'white',
                            color: THEME_COLORS.primary,
                            '&:hover': {
                              bgcolor: 'white',
                              color: '#1e1c4e'
                            }
                          }}
                        >
                          <PlayIcon />
                        </IconButton>
                      </Box>
                      {tutorial.completed && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '50%',
                            width: 30,
                            height: 30,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <CompletedIcon sx={{ color: '#4CAF50' }} />
                        </Box>
                      )}
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" fontWeight="medium" gutterBottom noWrap>
                        {tutorial.title}
                      </Typography>
                      
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          {tutorial.subject} · Grade {tutorial.grade}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tutorial.duration}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {tutorial.views} views
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleMenuOpen(e, tutorial.id)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        )}
        
        {activeTab === 'resources' && (
          <Box>
            {filteredItems.resources.length === 0 ? (
              <Box textAlign="center" py={4}>
                <ResourceIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No resources found
                </Typography>
                <Typography color="text.secondary" paragraph>
                  {searchTerm ? 'Try changing your search criteria' : 'Add your first resource to get started'}
                </Typography>
                {!searchTerm && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      backgroundColor: THEME_COLORS.primary,
                      '&:hover': { backgroundColor: '#1e1c4e' },
                      mt: 2
                    }}
                  >
                    Add Resource
                  </Button>
                )}
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredItems.resources.map(resource => (
                  <Grid item xs={12} key={resource.id}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            bgcolor: resource.type === 'PDF' ? 'rgba(244, 67, 54, 0.1)' :
                                  resource.type === 'DOCX' ? 'rgba(33, 150, 243, 0.1)' :
                                  'rgba(158, 158, 158, 0.1)',
                            color: resource.type === 'PDF' ? '#F44336' :
                                  resource.type === 'DOCX' ? '#2196F3' :
                                  '#757575',
                            p: 1.5,
                            borderRadius: 2,
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <ArticleIcon />
                        </Box>
                        
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {resource.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {resource.subject} · Grade {resource.grade} · {resource.size}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" color="text.secondary" mr={2}>
                            {resource.downloads} downloads
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              borderColor: THEME_COLORS.primary,
                              color: THEME_COLORS.primary,
                              mr: 1,
                              '&:hover': {
                                borderColor: '#1e1c4e',
                                bgcolor: 'rgba(39, 35, 97, 0.04)'
                              }
                            }}
                          >
                            Download
                          </Button>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleMenuOpen(e, resource.id)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
        
        {activeTab === 'assessments' && (
          <Box>
            {filteredItems.assessments.length === 0 ? (
              <Box textAlign="center" py={4}>
                <AssessmentIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No assessments found
                </Typography>
                <Typography color="text.secondary" paragraph>
                  {searchTerm ? 'Try changing your search criteria' : 'Add your first assessment to get started'}
                </Typography>
                {!searchTerm && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      backgroundColor: THEME_COLORS.primary,
                      '&:hover': { backgroundColor: '#1e1c4e' },
                      mt: 2
                    }}
                  >
                    Create Assessment
                  </Button>
                )}
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredItems.assessments.map(assessment => (
                  <Grid item xs={12} sm={6} md={4} key={assessment.id}>
                    <Card sx={{ 
                      height: '100%', 
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 4
                      }
                    }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="h6" fontWeight="medium" gutterBottom>
                            {assessment.title}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleMenuOpen(e, assessment.id)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {assessment.subject} · Grade {assessment.grade}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">
                            {assessment.questions} questions
                          </Typography>
                          <Typography variant="body2">
                            {assessment.duration}
                          </Typography>
                        </Box>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="body2" color="text.secondary">
                            Deadline: {new Date(assessment.deadline).toLocaleDateString()}
                          </Typography>
                          {getStatusChip(assessment.status)}
                        </Box>
                        
                        {assessment.status === 'completed' ? (
                          <Box display="flex" alignItems="center" justifyContent="center" bgcolor="rgba(76, 175, 80, 0.08)" p={1} borderRadius={1}>
                            <CompletedIcon sx={{ color: '#4CAF50', mr: 1 }} />
                            <Typography fontWeight="medium" color="#4CAF50">
                              Score: {assessment.score}
                            </Typography>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            fullWidth
                            sx={{
                              bgcolor: THEME_COLORS.primary,
                              '&:hover': { bgcolor: '#1e1c4e' }
                            }}
                          >
                            {assessment.status === 'in-progress' ? 'Continue' : 'Start'} Assessment
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            {activeTab === 'tutorials' && (
                <TutorialManagement userId={userId} />
            )}
          </Box>
        )}
      </Paper>
      
      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditSubject(subjects.find(s => s.id === selectedItemId))}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Subject</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleViewSubject(selectedItemId);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <CourseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {activeTab === 'tutorials' && (
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <PlayIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Watch Tutorial</ListItemText>
          </MenuItem>
        )}
        {activeTab === 'resources' && (
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <ArticleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        )}
        {activeTab === 'assessments' && (
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <AssessmentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Take Assessment</ListItemText>
          </MenuItem>
        )}
      </Menu>
  
      {/* Forms */}
      <SubjectForm 
        open={isSubjectFormOpen}
        onClose={handleSubjectFormClose}
        initialData={selectedSubject}
        userId={userId}
      />
      
      <TopicForm
        open={isTopicFormOpen}
        onClose={handleTopicFormClose}
        onSubmit={() => {
          // Reload data after topic is saved
          loadData();
        }}
        initialData={selectedTopic}
        subjectId={selectedTopic?.subjectId || 
                  (viewSubjectId || (subjects.length > 0 ? subjects[0].id : null))}
        userId={userId}
      />
      
      <SubtopicForm
        open={isSubtopicFormOpen}
        onClose={handleSubtopicFormClose}
        onSubmit={() => {
          // Reload data after subtopic is saved
          loadData();
        }}
        initialData={selectedSubtopic}
        topic={selectedTopic}
        subjectId={selectedTopic?.subjectId || 
                   (viewSubjectId || (subjects.length > 0 ? subjects[0].id : null))}
        userId={userId}
      />
    </Box>
  );
};

export default OnlineLearning;